import axiosConfig from "@/api/axiosConfig.js";
import CountryHelper from "@/assets/countryhelper.js";
import appToast from "@/appToast.js";

function IDSerializer(IDs) {
  return IDs.reduce((query, ID, i) => i === 0 ? `${query}ID=${ID}` : `${query}&ID=${ID}`, '');
}

const dataProvider = {
  // #region utility methods
  serviceResponseError: new function ServiceResponseError(){},
  processServiceResponse(serviceResponse, { showSuccess = false, successDetail = 'Request successful.' } = {}) {
    if(serviceResponse.IsSuccess) {
      if(showSuccess) {
        appToast({
          severity: "success",
          summary: "Success",
          detail: successDetail || serviceResponse.Message,
          life: 2000
        });
      }

      return serviceResponse.Data;
    } else {
      appToast({
        severity: "error",
        summary: "Error",
        detail: serviceResponse.Message || "An error occurred while making the request.",
        life: 5000
      });

      return this.serviceResponseError;
    }
  },
  // #endregion

  // #region Permissions

  async GetPermsGroups(portalId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/groups`, {
        params: {
          portalId
        },
      })
      .then((res) => res.data);

    return result;
  },

  async SavePermsGroup(customerGroup) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/perms/group`, customerGroup)
      .then((res) => res.data);

    return result;
  },
  
  async UpdatePermsGroup(customerGroup) {
      let result = await axiosConfig
      .patch(`${process.env.VUE_APP_API_WEB_URL}/perms/group`, customerGroup)
      .then((res) => res.data);

    return result;
  },

  async DeletePermsGroup(customerGroup) {
    let {
      PortalID: portalId,
      CustomerGroupID: customerGroupId
    } = customerGroup;
    
    let result = await axiosConfig
      .delete(`${process.env.VUE_APP_API_WEB_URL}/perms/group`, {
        data: {
          portalId,
          customerGroupId
        }
      })
      .then((res) => res.data);

    return result;
  },

  async GetPermsCustomers(portalId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/customers`, {
        params: {
          portalId
        },
      })
      .then((res) => res.data);

    return result;
  },

  async SavePermsCustomers(portalId, customerGroupId, customers) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/perms/customers`, {
        portalId,
        customerGroupId,
        customerIds: customers.map(c => c.CustomerID)
      })
      .then((res) => res.data);

    return result;
  },

  async GetPermsRoles() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/roles`)
      .then((res) => res.data);

    return result;
  },

  async GetPermsRolePermissions(roleId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/rolePermissions`, {
        params: {
          roleId
        },
      })
      .then((res) => res.data);

    return result;
  },

  async SavePermsRolePermissions(roleId, featureIds) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/perms/rolePermissions`, {
          roleId,
          featureIds
      })
      .then((res) => res.data);

    return result;
  },

  async GetEmployees(portalId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/employees`, {
        params: {
          portalId
        },
      })
      .then((res) => res.data);

    return result;
  },

  async GetPermsEmployeeRoles(employeeId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/employeeRoles`, {
        params: {
          employeeId
        },
      })
      .then((res) => res.data);

    return result;
  },

  async SavePermsEmployeeRoles(portalId, employeeId, roleIds) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/perms/employeeRoles`, {
          portalId,
          employeeId,
          roleIds
      })
      .then((res) => res.data);

    return result;
  },

  async GetPermsEmployeeGroups(portalId, employeeId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/employeeGroups`, {
        params: {
          portalId,
          employeeId
        },
      })
      .then((res) => res.data);

    return result;
  },

  async SavePermsEmployeeGroups(portalId, employeeId, groups) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/perms/employeeGroups`, {
          portalId,
          employeeId,
          groups
      })
      .then((res) => res.data);

    return result;
  },

  async GetPermsEmployeeKPIs(employeeId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/employeeKPIs`, {
        params: {
          employeeId
        },
      })
      .then((res) => res.data);

    return result;
  },

  async SavePermsEmployeeKPIs(employeeId, featureIds) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/perms/employeeKPIs`, {
          employeeId,
          featureIds
      })
      .then((res) => res.data);

    return result;
  },

    async GetPermsFeatures() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/features`)
      .then((res) => res.data);

    return result;
  },

  async GetPermsEmployeeFeatures(portalId, employeeId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/perms/employeeFeatures`, {
        params: {
          portalId,
          employeeId
        },
      })
      .then((res) => res.data);

    return result;
  },

  // #endregion

  async GetPortals() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Master/Portals`)
      .then((res) => res.data);

    return result;
  },

  async GetLoadLocations(loadID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getLoadLocations`, {
        params: {
          loadID
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLoadLock(loadid) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getLoadLock`, {
        params: {
          loadid
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLBolCodes() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Master/getltlbolcodes`, {
        params: {

        },
      })
      .then((res) => res.data);

    return result;
  },
  async edireadytobill(loadid) {
    let result = await axiosConfig
        .post(`${process.env.VUE_APP_API_WEB_URL}/edi/readytobill`, {},{
        params: {
          loadid
        },
      })
      .then((res) => res.data);
    return result;
  },
  async UpdateInOutLocations(postBody) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/updateinoutlocations`, postBody)
      .then((res) => res.data);
    return result;
  },

  async GetLoadLocationsInAndOut(loadLocId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getinoutlocations`, {
        params: {
              loadlocationid:loadLocId
        },
      })
      .then((res) => res.data);

    return result;
  },
  async IsLoadExists(loadId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/IsLoadExist`, {
        params: {
            loadID:loadId
        },
      })
      .then((res) => res.data);

    return result;
  },
  async getCityStateZipAutoComplete(CityName, StateName, PostalCode, CountryName) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Master/CityStateZipAutoComplete`, {
        CityName,
        StateName,
        PostalCode,
        CountryName
      })
      .then((res) => res.data);
    return result;
  },
  async GetLTLCustomerMargin(CustomerId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLCustomer/GetLTLCustomerMargin`, {
        params: {
              CustomerId:CustomerId
        },
      })
      .then((res) => res.data);

    return result;
  },
  async insertAckowledgement(ackBody) {

    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Master/insertacknowledgement`,
        ackBody
      )
      .then((res) => res.data);

    return result;
  },
  async GetLTLLoadAccesorials() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/GetLTLLoadAccesorials`, {
        params: {

        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLLoadTracking(shipmentid) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/getltltracking`, {
        params: {
          shipmentId: shipmentid
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLDeliveryReceipt(loadid) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/GetLTLDeliveryReceipt`, {
        params: {
          loadId: loadid
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLShippingLabels(loadid) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/GetLTLShippingLabels`, {
        params: {
          loadId: loadid
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLBol(loadid) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/GetLTLBol`, {
        params: {
          loadId: loadid
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLCustomerHazmatContact(customerid) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLCustomer/GetLTLCustomerHazmatContact`, {
        params: {
          customerId: customerid
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLRateQuoteDocument(ltlRateBody) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/LTLRateQuotePDF`,
        ltlRateBody
      )
      .then((res) => res.data);
    return result;
  },
  async SaveAndTenderLTLLoad(ltlLoadPostBody,ltlTenderBody) {
    ltlTenderBody.pickupDate = ltlTenderBody.pickupDate.toISOString().split('T')[0]
    ltlTenderBody.deliveryDate =  ltlTenderBody.deliveryDate.toISOString().split('T')[0]
    ltlLoadPostBody.PickupLocation.PickupDate = ltlLoadPostBody.PickupLocation.PickupDate.toISOString().split('T')[0]
    ltlLoadPostBody.DestinationLocation.DeliveryDate = ltlLoadPostBody.DestinationLocation.DeliveryDate.toISOString().split('T')[0]
    
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/saveandtenderLTLLoad`,
        {
          lTLLoadSavePost:ltlLoadPostBody,
          lTLTenderPostBody:ltlTenderBody
        }

      )
      .then((res) => res.data);
    return result;
  },
async SaveLTLLoad(ltlLoadPostBody,ltlTenderBody) {
    ltlTenderBody.pickupDate = ltlTenderBody.pickupDate.toISOString().split('T')[0]
    ltlTenderBody.deliveryDate =  ltlTenderBody.deliveryDate.toISOString().split('T')[0]
    ltlLoadPostBody.PickupLocation.PickupDate = ltlLoadPostBody.PickupLocation.PickupDate.toISOString().split('T')[0]
    ltlLoadPostBody.DestinationLocation.DeliveryDate = ltlLoadPostBody.DestinationLocation.DeliveryDate.toISOString().split('T')[0]

    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/saveLTLLoad`,
        {
        lTLLoadSavePost:ltlLoadPostBody,
        lTLTenderPostBody:ltlTenderBody
        }
      )
      .then((res) => res.data);
    return result;
  },
  async GetCityStateByZip(zip, country) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Master/CityStateByZip`, {
        params: {
          zip,
          country
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLCarrierBySCAC(scac) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLCarrier/GetCarrierBySCAC`, {
        params: {
          SCAC: scac
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLCarriers() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLCarrier/GetLTLCarriers`)
      .then((res) => res.data);

    return result;
  },
  async GetLTLVolumeRateQuote(ltlRateBody) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/GetVLTLRateQuote`,
        ltlRateBody
      )
      .then((res) => res.data);
    return result;
  },
  async GetLTLRateQuote(ltlRateBody) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/LTLRateQuote`,
        ltlRateBody
      )
      .then((res) => res.data);
    return result;
  },
  async SaveLTLLoadNotes(ltlLoadNotePostBody) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/saveLTLLoadNote`,
        ltlLoadNotePostBody
      )
      .then((res) => res.data);
    return result;
  },
  async GetLTLLoadNotes(loadId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/getLTLLoadNotes/`, {
        params: {
          loadId: loadId
        }
      })
      .then((res) => res.data);
    return result;
  },

  async GetLTLCustomerNotes(customerId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLCustomer/getCustomerNotes`, {
        params: {
          customerId: customerId
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLtlLoad(loadId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/getLTLLoad`, {
        params: {
          loadId: loadId
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLtlLoadCopy(loadId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/getLTLLoadCopy`, {
        params: {
          loadId: loadId
        },
      })
      .then((res) => res.data);

    return result;
  },
  async GetLTLCustomerList(customerName = null) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/LTLCustomer/getLTLCustomersList`, {
        params: {
          CustomerName: customerName
        },
      })
      .then((res) => res.data);

    return result;
  },
  async ValidateNMFC(NMFC)
  {
    let result = await axiosConfig
    .get(`${process.env.VUE_APP_API_WEB_URL}/LTLLoads/ValidateNMFC`, {
        params: {
          NMFC: NMFC
        },
      })
      .then((res) => res.data);
    return result;
  },
  async InsertLocations(customerLocationPostBody) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Customer/insertCustomerLocation`,
        customerLocationPostBody
      )
      .then((res) => res.data);
    return result;
  },
  async loginToITS(employeeID, portalID, userName, password) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/posting/gettruckstoprefreshtoken`
        , {
          Username: userName,
          Password: password,
          EmployeeID: employeeID,
          PortalID: portalID,
        })
      .then((res) => res.data);
    return result;
  },
  async getCheckITSToken() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/checkitstoken`)
      .then((res) => res.data);
    return result;
  },

  async deleteDocuments(documentIds) {
    let result = await axiosConfig
      .delete(`${process.env.VUE_APP_API_WEB_URL}/Carrier/carrierdocuments`, {
        data: {
          DocumentIds: documentIds,
        },
      })
      .then((res) => res.data);
    return result;
  },

  async setEmployeeSettings(keycode, settingValue) {
    let result = await axiosConfig
      .put(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/saveEmployeeSetting`, {
        KeyCode: keycode,
        SettingValue: settingValue,
      })
      .then((res) => res.data);
    return result;
  },

  async getColumnSetting(keycode) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getEmployeeSetting`, {
        params: {
          KeyCode: keycode,
        },
      })
      .then((res) => res.data);

    return result;
  },
  async getChartData(TimezoneOffset) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/myLoadGraph`, {
        params: {
          TimezoneOffset: TimezoneOffset,
        },
      })
      .then((res) => res.data);

    return result;
  },
  async getNotificationCount() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Notification/notificationcount`)
      .then((res) => res.data);

    return result;
  },
  async getNotifications(pageNumber, pageSize, severityLevel) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Notification/notications`, {
        params: {
          pageNumber: pageNumber,
          pageSize: pageSize,
          severity: severityLevel,
        },
      })
      .then((res) => res.data);
    return result;
  },
  async updateNotifications(notificationids, status) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Notification/updateNotification`, {
        notificationID: notificationids,
        status: status,
      })
      .then((res) => res.data);
    return result;
  },
  async updateCarrierFallOff(carrierID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/updateCarrierFallOff`, {
        carrierID: carrierID,
      })
      .then((res) => res.data);
    return result;
  },
  async getCustomers(accOnhold = 0, customerName = null) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/customerSelectionFields`
        , {
          params: {
            AccountOnHold: accOnhold,
            CustomerName: customerName
          },
        })
      .then((res) => res.data);
    return result;
  },
  async getCarrierSelectionList(filterOnHold = true, uspsCarriersOnly = false) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/carrierSelectionFields`
        , {
          params: {
            FilterOnHold: filterOnHold,
            USPSCarriersOnly: uspsCarriersOnly
          },
        })
      .then((res) => res.data);
    return result;
  },
  async getCarrierNames(filterOnHold = true, uspsCarriersOnly = false, carrierName) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/carrierSelectionNames`
        , {
          params: {
            FilterOnHold: filterOnHold,
            USPSCarriersOnly: uspsCarriersOnly,
            CarrierName: carrierName
          },
        })
      .then((res) => res.data);
    return result;
  },
  async getCustomerNotes(custID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Customer/getCustomerNotes`
        , {
          CustomerID: custID

        })
      .then((res) => res.data);
    return result;
  },
  async getCustomerLocations(customerID, locTypeID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Customer/getCustomerLocation`
        , {
          LocationName: null,
          Address: null,
          City: null,
          State: null,
          PostalCode: null,
          CustomerID: customerID,
          LocTypeID: locTypeID
        })
      .then((res) => res.data);
        result.CustomerLocations.forEach(loc => {
        const mapped = CountryHelper.mapCountryStringToObject(loc.Country);
        loc.InvalidCountry = mapped?.invalid;
      });
    return result;
  },
  async getEquipmentTypes() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Master/equipments`)
      .then((res) => res.data);
    return result;
  },
  async getCustomerProfiles() {
    let result = await axiosConfig
      // .get(`${process.env.VUE_APP_API_WEB_URL}/Master/customerProfiles`)
      .get(`${process.env.VUE_APP_API_WEB_URL}/Master/customers`)
      .then((res) => res.data);
    return result;
  },
  async SaveCarrierNotes(carrierID,notes) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/saveCarrierNotes`, {
        CarrierID: carrierID,
        Note: notes
      })
      .then((res) => res.data);
    return result;
  },
  async getCityState(CustomerID, CityName, StateName, LocTypeID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Master/CityStateNew`, {
        CustomerID: CustomerID,
        CityName: CityName,
        StateName: StateName,
        LocTypeID: LocTypeID,
      })
      .then((res) => res.data);
    return result;
  },
  async getCityStateZip(CityName, StateName, PostalCode, CountryName) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Master/CityStateZip`, {
        CityName,
        StateName,
        PostalCode,
        CountryName
      })
      .then((res) => res.data);
    return result;
  },
  async saveCarrierLoad(saveCarrierLoadData) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/SourceLoad/SaveCarrier`, {
        CarrierPickID: parseInt(saveCarrierLoadData.CarrierPickID),
        CarrierID: parseInt(saveCarrierLoadData.CarrierID),
        LoadID: parseInt(saveCarrierLoadData.LoadID),
        EmployeeID: 0,
        Primary: saveCarrierLoadData.Primary,
        Dispatcher: saveCarrierLoadData.Dispatcher,
        ELDEnabled: saveCarrierLoadData.ELDEnabled,
        AssistingPortalID: parseInt(saveCarrierLoadData.AssistingPortalID),
        AssistingEmployeeID: parseInt(saveCarrierLoadData.AssistingEmployeeID),
      })
      .then((res) => res.data);
    return result;
  },
  async saveLoadCreateALoad(saveInitialLoad) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/saveLoadCreateALoad`, {
        LoadType: parseInt(saveInitialLoad.LoadType),
        CustomerID: saveInitialLoad.CustomerID,
        CustomerName: saveInitialLoad.CustomerName,
        PickupCity: saveInitialLoad.PickupCity,
        PickupState: saveInitialLoad.PickupState,
        PickupDate: saveInitialLoad.PickupDate,
        PickupTime: saveInitialLoad.PickupTime,
        DestinationCity: saveInitialLoad.DestinationCity,
        DestinationState: saveInitialLoad.DestinationState,
        DestinationDate: saveInitialLoad.DestinationDate,
        DestinationTime: saveInitialLoad.DestinationTime,
        Commodity: saveInitialLoad.Commodity,
        PickupWeight: parseInt(saveInitialLoad.PickupWeight),
        Mileage: parseInt(saveInitialLoad.Mileage),
        EquipmentID: saveInitialLoad.EquipmentID,
        EquipMinSize: parseInt(saveInitialLoad.EquipMinSize),
        TeamService: saveInitialLoad.TeamService,
        PartialLoad: saveInitialLoad.PartialLoad,
        PayRange: saveInitialLoad.PayRange,
        Comments: saveInitialLoad.Comments,
        PostRate: saveInitialLoad.PostRate,
        PostComments: saveInitialLoad.PostComments,
        DATEnabled: saveInitialLoad.DATEnabled,
        TruckstopEnabled: saveInitialLoad.TruckstopEnabled,
        PEPEnabled: saveInitialLoad.PEPEnabled,
        NetworkLoad: saveInitialLoad.NetworkLoad,
        ActionType: saveInitialLoad.ActionType,
        TimezoneOffset: saveInitialLoad.TimezoneOffset,
        LoadDate: saveInitialLoad.LoadDate,
        AIAgent: saveInitialLoad.AIAgent
      })
      .then((res) => res.data);
    return result;
  },
  async deleteDriver(driverID, carrierID) {
    let result = await axiosConfig
      .delete(`${process.env.VUE_APP_API_WEB_URL}/Carrier/DeleteCarrierDriver`, {
        data: {
          carrierDriverID: parseInt(driverID),
          CarrierID: parseInt(carrierID),
        },
      })
      .then((res) => res.data);
    return result;
  },
  async saveCarrierDriver(saveCarrierDriver) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/saveUpdateCarrierDriver`, {
        DriverID: parseInt(saveCarrierDriver.DriverID),
        FirstName: saveCarrierDriver.FirstName,
        LastName: saveCarrierDriver.LastName,
        PhoneNumber: saveCarrierDriver.PhoneNumber,
        TractorNo: saveCarrierDriver.TractorNo,
        TrailerNo: saveCarrierDriver.TrailerNo,
        Notes: saveCarrierDriver.Notes,
        CarrierID: parseInt(saveCarrierDriver.CarrierID),
        PortalID: parseInt(saveCarrierDriver.PortalID),
      })
      .then((res) => res.data);
    return result;
  },
  async getSourceLoadData(id) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/SourceLoad/SourceLoadDetails`, {
        params: {
          LoadID: id,
        },
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierProfileGeneralTabDetails(id) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/carrierProfileGeneralTabInfo`, {
        CarrierID: id,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierSafetyTabDetails(id, mc, usdot) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/getCarrierSafetyProfile`, {
        CarrierID: id,
        CarrierMCNum: mc,
        USDOTNumber: usdot,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierInsuranceTabDetails(id, mc, usdot) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/carrier-insuranceInformation`, {
        CarrierID: id,
        CarrierMCNum: mc,
        USDOTNumber: usdot,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierProfileTabDetails(id) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/carrier-profileInformation`, {
        CarrierID: id,
      })
      .then((res) => res.data);
    return result;
  },
  // async getCarrierProfileHistoryTabInfo(params) {
  //   let result = await axiosConfig
  //     .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/carrierProfileHistoryTabInfo`, {
  //       CarrierID: params.lazyEvent.CarrierID,
  //       TimezoneOffset: params.lazyEvent.TimezoneOffset,
  //       First: params.lazyEvent.first,
  //       Page: params.lazyEvent.page,
  //       Rows: params.lazyEvent.rows,
  //       SortOrder:
  //         params.lazyEvent.sortOrder == null ? -1 : params.lazyEvent.sortOrder,
  //       SortField:
  //         params.lazyEvent.sortField == null || params.lazyEvent.sortField == ""
  //           ? "PickupDate"
  //           : params.lazyEvent.sortField,
  //     })
  //     .then((res) => res.data);
  //   return result;
  // },
  async getAllHistoryFilterOptions(id, calName) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Carrier/carrierlocations`, {
        params: {
          CarrierID: id,
          tabID: calName,
          timeZoneOffset: new Date().getTimezoneOffset() * -1
        },
      })
      .then((res) => res.data);
    return result;
  },

  async getCarrierProfileDocumentTabInfo(params) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/carrierdocumentdetails`, {
        CarrierID: params.lazyEvent.CarrierID,
        DocumentGroup: params.lazyEvent.DocumentGroup,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierDetails(id) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/getCarrierProfile`, {
        CarrierID: id,
        CarrierMCNum: "",
        USDOTNumber: "",
      })
      .then((res) => res.data);
    return result;
  },
  async lookupCarrier(lookupFieldAndValue) {
    const defaults = {
      CarrierMCNum: null,
      USDOTNumber: null,
      DOTNumber: null
    };
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/lookupCarrier`, {
        ...defaults,
        ...lookupFieldAndValue
      })
      .then((res) => res.data);
    return result;
  },
  async getLineItemDefinitions(LoadTypeID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Master/lineitemdefinitions`, { params: { LoadTypeID } })
      .then((res) => res.data);
    return result;
  },
   async getTrackandtrace(loadId){
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Tracking/gettrackandtrace/${loadId}`)
      .then((res) => res.data);
    return result;
  },

  async cancelTracking(loadID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Tracking/stoptracking/${loadID}`)
      .then((res) => res);

    return result;
  },
  async deleteSubscription(LoadTrackingSubscriptionID) {
    let result = await axiosConfig
      .delete(`${process.env.VUE_APP_API_WEB_URL}/Tracking/deletesubscription/${LoadTrackingSubscriptionID}`)
      .then((res) => res);

    return result;
  },
   async saveSubscription(subscription)
   {
    subscription.forEach(element => {
        if(element.LoadTrackingSubscriptionID==0)
        {
        let result = axiosConfig
          .post(`${process.env.VUE_APP_API_WEB_URL}/Tracking/insertsubscription`, {
            LoadTrackingSubscriptionID: parseInt(element.LoadTrackingSubscriptionID),
            LoadID: parseInt(element.LoadID),
            EmailAddress: element.EmailAddress,
            NotificationFrequency: parseInt(element.NotificationFrequency),
            IsIndividual: element.IsIndividual,
          })
          .then((res) => res);
        return result;
      }
        else{
        //update
        let result = axiosConfig
          .put(`${process.env.VUE_APP_API_WEB_URL}/Tracking/updatesubscription`, {
            LoadTrackingSubscriptionID: parseInt(element.LoadTrackingSubscriptionID),
            LoadID: parseInt(element.LoadID),
            EmailAddress: element.EmailAddress,
            NotificationFrequency: parseInt(element.NotificationFrequency),
            IsIndividual: element.IsIndividual,
          })
          .then((res) => res);
        return result;
      }
    });
  },
  async getTrackingSubscriptions(loadId) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Tracking/getsubscriptions/${loadId}`)
      .then((res) => res.data);
    return result;
  },
  async getCarrierFilter(filterData, filterName, loadType) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/SourceLoad/sourceLoadFilterData`, {
        params: {
          filterData: filterData,
          filterName: filterName,
          loadType: loadType
        },
      })
      .then((res) => res.data);
    return result;
  },
  async getFavoriteCarrierFilter(filterData, loadType) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/SourceLoad/sourceLoadFavoriteFilterData`, {
        params: {
          filterData: filterData,
          loadType: loadType
        },
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierPerformance(carrierID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/carrier/getCarrierPerformance`, {
        carrierID: carrierID,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierLanes(carrierID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/getCarrierLanes`, {
        carrierID: carrierID,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierContact(carrierID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/getCarrierContacts`, {
        carrierID: carrierID,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierOffice(carrierID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/getCarrierOffices`, {
        carrierID: carrierID,
      })
      .then((res) => res.data);
    return result;
  },
  async getCarrierNotes(carrierID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/carrier/getCarrierNotes`, {
        CarrierID: carrierID,
      })
      .then((res) => res.data);
    return result;
  },
  async getDriversData(carrierID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/carrier/getCarrierDrivers`, {
        carrierID: carrierID,
      })
      .then((res) => res.data);
    return result;
  },
  async getLoadBoardAccess() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/loadBoardAccess`)
      .then((res) => res.data);
    return result;
  },
  async cancelLoads(empid, loadids) {
    let result = await axiosConfig
      .delete(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/cancelLoads`, {
        data: {
          EmployeeID: empid,
          LoadIDs: loadids.split(",").map(Number),
        },
      })
      .then((res) => res.data);
    return result;
  },
  async unpostLoads(loadids) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/multiunpostloads`, {
        LoadIDs: loadids,
      })
      .then((res) => res.data);
    return result;
  },
  async getTrackLoadMilege(
    pickupcity,
    pickupstate,
    destinationcity,
    destinationstate
  ) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/trackLoadMilege`, {
        pickupLocations: [
          {
            City: pickupcity,
            State: pickupstate,
          },
        ],
        dropLocations: [
          {
            City: destinationcity,
            State: destinationstate,
          },
        ],
      })
      .then((res) => res.data);
    return result;
  },
  async getMultipleTrackLoadMilege(
    pickupLocations,
    dropLocations
  ) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/trackLoadMilege`, {
        pickupLocations,
        dropLocations
      })
      .then((res) => res.data);
    return result;
  },
  async uploadDocs(
    agentDocsData
  ) {

    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/uploadDocs`,
        agentDocsData
      )
      .then((res) => res.data);
    return result;
  },
  async getSourceLoadRateCost(
    pickupcity,
    pickupstate,
    destinationcity,
    destinationstate,
    equipementIDs
  ) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/SourceLoad/getSourceLoadRateCost`, {
        PickupCity: pickupcity,
        PickupState: pickupstate,
        DestinationCity: destinationcity,
        DestinationState: destinationstate,
        EquipmentIDs: equipementIDs,
      })
      .then((res) => res.data);
    return result;
  },
  async setFavourite(carrierid, isfavourite) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/carrier/AddOrRemoveCarrierFromFavorite`, {
        CarrierID: carrierid,
        IsFavoriteCarrier: isfavourite,
      })
      .then((res) => res.data);
    return result;
  },
  async createHelpDeskTicket(carrierid, MCNum, USDot, ticket, details = null) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/carrier/HelpDeskTicket`, {
        CarrierID: parseInt(carrierid),
        CarrierMCNum: MCNum,
        USDOTNumber: USDot,
        Description: details,
        ticketType: ticket,
      })
      .then((res) => res.data);
    return result;
  },
  async GetEditLoadDetails(loadID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getCurrentLoad`, {
        LoadID: loadID
      })
      .then((res) => res.data);
    return result;
  },
  async updateLoadLock(
    LoadID,
    LockedByID,
    LockedByUser,
    LockLoad,
    isFromLeaving
  ) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/updateLoadLock`, {
        LoadID,
        LockedByID,
        LockedByUser,
        LockLoad,
        isFromLeaving
      })
      .then((res) => res.data);

    return result;
  },

  async SelectCarrier(carrierID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/SourceLoad/SelectCarrier`, {
        params: {
          CarrierId: carrierID,
        },
      })
      .then((res) => res.data);

    return result;
  },

  async deleteSelectedLDIDocs(DocsIDs) {
    let result = await axiosConfig
      .delete(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/deleteDocuments`,
        {
          data: {
            deleteDocDetails: DocsIDs
          }
        }
      )
      .then((res) => res.data);
    return result;
  },

  async sendToHubTran(DocsIDs) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/sendToHubTran`, {
        DocumentOwnerID: DocsIDs.DocumentOwnerID,
        DocumentFrom: DocsIDs.DocumentFrom,
        getDocumentDetails: DocsIDs.getDocumentDetails
      }
      )
      .then((res) => res.data);
    return result;
  },

  async GetLDIAgentDocuments(DocumentGroupID, DocumentOwnerID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getLoadDocuments`, {
        DocumentGroupID: DocumentGroupID,
        DocumentOwnerID: DocumentOwnerID
      })
      .then((res) => res.data);
    return result;
  },

  // --- Reports Center API methods ---
  async getReportTree() {
    try {
      let result = await axiosConfig
        .get(`${process.env.VUE_APP_API_WEB_URL}/report/report-center`)
        .then((res) => res.data);
      
      // Parse JSON string if needed
      if (typeof result === 'string') {
        result = JSON.parse(result);
      }
      
      // Check if result is an array
      if (!Array.isArray(result)) {
        console.error('Expected array but got:', typeof result, result);
        return [];
      }
      
      // Transform flat report array into tree structure
      if (result && result.length > 0) {
        return [
          {
            key: 'all-reports',
            label: 'Available Reports',
            children: result.map((report) => ({
              key: `report-${report.ReportID}`,
              label: report.Title,
              data: {
                reportId: report.ReportID,
                name: report.Title,
                parameters: report.Parameters || []
              }
            }))
          }
        ];
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching report tree:', error);
      return [];
    }
  },

  async getReportParams(reportData) {
    // reportData contains the full report info including parameters
    if (!reportData || !reportData.parameters) return [];
    
    return reportData.parameters.map((param) => {
      let options = param.Options;
      if (typeof options === 'string') {
        try {
          options = JSON.parse(options);
        } catch (error) {
          console.error('Invalid report parameter options:', param.ParameterName, error);
          options = [];
        }
      }

      return {
        id: param.ReportParameterID,
        name: param.ParameterName,
        label: param.ParameterLabel,
        type: param.ParameterType,
        required: param.IsRequired,
        visible: param.Visible,
        options: Array.isArray(options) ? options.map((option) => ({
          ...option,
          label: option.label ?? option.Label,
          value: option.value ?? option.Value
        })) : [],
        props: {}
      };
    });
  },
  
  async getCustomersReporting(portalID = null, accountManagerID = null) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Report/customers`
        , {
          params: {
            PortalID: portalID,
            AccountManagerID: accountManagerID
          },
        })
      .then((res) => res.data);
    return result;
  },

  async fetchReportPdf(reportId, reportParams) {
    const body = { reportId, reportParams, format: "PDF" };
    const result = await axiosConfig.post(
      `${process.env.VUE_APP_API_WEB_URL}/report/get-report`,
      body
    ).then(res => res.data);
    
    // Convert base64 string to Blob URL
    if (result) {
      try {
        const byteCharacters = atob(result);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);
        return { pdfUrl };
      } catch (error) {
        console.error('Error converting base64 to PDF:', error);
        throw new Error('Failed to convert PDF data');
      }
    }
    
    return { pdfUrl: '' };
  },

  async fetchReportExcel(reportId, reportParams) {
    const body = { reportId, reportParams, reportFormat: "EXCELOPENXML" };
    const result = await axiosConfig.post(
      `${process.env.VUE_APP_API_WEB_URL}/report/get-report`,
      body
    ).then(res => res.data);
    
    // Convert base64 string to Blob URL
    if (result) {
      try {
        const byteCharacters = atob(result);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const excelUrl = URL.createObjectURL(blob);
        return { excelUrl };
      } catch (error) {
        console.error('Error converting base64 to Excel:', error);
        throw new Error('Failed to convert Excel data');
      }
    }
    
    return { excelUrl: '' };
  },

  async GetLoadNotes(loadID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/loadnotes/getLoadNotes`, {
        params: {
          LoadID: loadID,
        },
      })
      .then((res) => res.data);
    return result;
  },

  async getPODUnsatReasons() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getPODUnsatReasons`, {})
      .then((res) => res.data);
    return result;
  },
  async getRateConFormData(loadID) {

    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getRateConFormData`, {
        params: {
          LoadID: loadID,
        },
      })
      .then((res) => res.data);
    return result;
  },

  async manuallySignRateConfirmation(rateConfirmationID) {

    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/manuallySignRateConfirmation`, {
        params: {
          RateConfirmationID: rateConfirmationID,
        }
      })
      .then((res) => res.data);
    return result;
  },

  async sendForSigning(request) {

    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/sendForSigning`,
        request
      )
      .then((res) => res.data);
    return result;
  },

  async getRateConReportData(rateConReportRequest, bolDetailRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getRateConBOLReport`, {
        rateConReportRequest: rateConReportRequest,
        bolDetailRequest: bolDetailRequest
      })
      .then((res) => res.data);
    return result;
  },

  async SaveLoadBookEdit(loadDetailRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/saveLoadBookEdit`,
        loadDetailRequest
      )
      .then((res) => res.data);
    return result;
  },

  async refreshLoadPosting(LoadID, PortalID, StatusID) {
    let result = await axiosConfig
    .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/refreshLoadPosting`, {
      LoadID: LoadID,
      StatusID: StatusID,
      PortalID: PortalID
    })
    .then((res) => res.data);
    return result;
  },

  async SaveCarrierSafetyInfo(carrierSafetyRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/saveCarrierSafetyInfo`,
        carrierSafetyRequest
      )
      .then((res) => res.data);
    return result;
  },

  async overrideCarrier(overrideCarrierRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/overrideCarrier`,
        overrideCarrierRequest
      )
      .then((res) => res.data);
    return result;
  },

  async checkOverridePassword(password) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/checkOverridePassword`, 
        password,
      )
      .then((res) => res.data);
    return result;
  },

  async SaveCarrierProfileInfo(carrierProfileRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/saveCarrierProfileInformation`,
        carrierProfileRequest
      )
      .then((res) => res.data);
    return result;
  },

  async saveLoadNotes(loadID, notes, commodity, isEmailChecked, emailID, localeDateTime) {

    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/loadnotes/saveLoadNotes`, {
        LoadID: loadID,
        Note: notes,
        Commodity: commodity,
        IsEmailChecked: isEmailChecked,
        EmailID: emailID,
        LocaleDateTime: localeDateTime
      })
      .then((res) => res.data);
    return result;
  },

  async startTracking(loadID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Tracking/starttracking/${loadID}`)
      .then((res) => res);
    return result;
  },

  async updateTracking(loadID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Tracking/updatetracking/${loadID}`)
      .then((res) => res);
    return result;
  },

  async GetTrackingSettings(loadID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Tracking/gettrackingsettings/${loadID}`)
      .then((res) => res.data);
    return result;
  },

  async saveTrackingSetting(TrackingSettingsRequest) {
    let trackingSettings = TrackingSettingsRequest;
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Tracking/inserttrackingsettings`,
        trackingSettings)
      .then((res) => res);
    return result;
  },

  async updateTrackingSetting(TrackingSettingsRequest) {
    let trackingSettings = TrackingSettingsRequest;
    let result = await axiosConfig
      .put(`${process.env.VUE_APP_API_WEB_URL}/Tracking/updatetrackingsettings`,
        trackingSettings)
      .then((res) => res);
    return result;
  },
  async getNetworkLoadInfo(PortalID , IsRetrieveEmployees){
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getNetworkLoadInfo`, {
        PortalID: PortalID,
        IsRetrieveEmployees: IsRetrieveEmployees
      })
      .then((res) => res.data);
    return result;
  },
  async GetCOMCheckData(loadID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getComcheck`, {
        params: {
          LoadID: loadID,
        },
      })
      .then((res) => res.data);
    return result;
  },

  async CreateComcheck(comcheckRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/createComcheck`,
        comcheckRequest
      )
      .then((res) => res.data);
    return result;
  },

  async DeleteComcheck(deleteRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/deleteComcheck`,
        deleteRequest
      )
      .then((res) => res.data);
    return result;
  },

  async ResendComcheckEmail(resendRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/resendComcheckExpressCode`,
        resendRequest
      )
      .then((res) => res.data);
    return result;
  },

  async GetComcheckTypes() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Master/comcheckTypes`)
      .then((res) => res.data);
    return result;
  },

  async GetComcheckPermissions(loadID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getComcheckPermissions`, {
        params: { loadID }
      })
      .then((res) => res.data);
    return result;
  },

  async GetBOLReport(BOLDetailRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/GetBOLReport`,
        BOLDetailRequest
      )
      .then((res) => res.data);
    return result;
  },

  async copyLoad(CopyLoadReq) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/copyLoad`,
        CopyLoadReq
      )
      .then((res) => res.data);
    return result;
  },

  async generateInvoice(invID, loadID, loadType) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Trackloads/generateInvoice`, { params: { invID: invID, loadID: loadID, loadType: loadType} })
      .then((res) => res.data);
    return result;
  },
  
  async retrieveDocument(DocumentPath){
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/retrieveDocByPath`,
        DocumentPath
      )
      .then((res) => res.data)
      .catch(
        function (error) {
          console.log(error);
          return ""
        }
      )

    return result;
  },

  async moveDocuments(DocumentIds) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/moveDocuments`, {
        DocumentID: DocumentIds
      }
      )
      .then((res) => res.data);
    return result;
  },
  async getRoutingDetails(milesRoute) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/milesRoute`,
        milesRoute
      )
      .then((res) => res.data);
    return result;
  },

  async sendEmail(request) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Email/SendEmail`,
        request
      )
      .then((res) => res.data);
    return result;
  },

  async sendEmailWithDocuments(request) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Email/SendEmailWithDocuments`,
        request
      )
      .then((res) => res.data);
    return result;
  },

  async sendEmailWithAttachments(request) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Email/SendEmailWithBase64Attachments`,
        request
      )
      .then((res) => res.data);
    return result;
  },
  async sendCSSEmailDetail(params) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/cssSendEmailDetail`, {
        LoadID: '',
        Origin: params.Origin,
        PickupDate: params.PickupDate,
        Destination: params.Destination,
        DeliveryDate: params.DropDate,
        Commodity: params.Commodity,
        Weight: params.Weight,
        EquipmentSize: params.Size,
        EquipmentType: params.Equipment

      }
      )
      .then((res) => res.data);
    return result;
  },

  async sendEmailDetail(ownerID, employeeID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/sendEmailDetail`, {
        OwnerID: ownerID,
        EmployeeID: employeeID
      }
      )
      .then((res) => res.data);
    return result;
  },
  async sendPacket(request) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/sendPacket`,
        request
      )
      .then((res) => res.data);
    return result;
  },
  async sendPDFPacket(request) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Carrier/sendPDFPacket`,
        request
      )
      .then((res) => res.data);
    return result;
  },
  async acknowledgeBillingInstructions(request) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Invoice/acknowledgeBillingInstructions`,
        request
      )
      .then((res) => res.data);
    return result;
  },
  async getEDI204LoadTenders() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/EDI/getTenders`)
      .then((res) => res.data);
    return await result;
  },
  async getEDI204LoadTenderDetails(params) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/EDI/getTenderDetails`, {
        params
      })
      .then((res) => res.data);
    return result;
  },
  async acceptEDI204LoadTender(params) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/EDI/AcceptTender`,
        params
      )
      .then((res) => res.data);
    return result;
  },
  async declineEDI204LoadTender(params) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/EDI/DeclineTender`,
        params
      )
      .then((res) => res.data);
    return result;
  },
  async getSDResons() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Trackloads/GetShippingDelayReasons`)
      .then((res) => res.data);
    return await result;
  },
  async saveShippingDelay(loadID, createdBy, loadLocID, shippingDelayReasonID, newETA, comments) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Trackloads/SaveShippingDelay`, {
        LoadID: loadID,
        CreatedBy: createdBy,
        LoadLocID: loadLocID,
        ShippingDelayReasonID: shippingDelayReasonID,
        NewETA: newETA,
        Comments: comments
      })
      .then((res) => res.data);
    return result;
  },
  async getLoadDrops(loadID) {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Trackloads/getLoadDrop`, {
        params: {
          LoadID: loadID,
        },
      })
      .then((res) => res.data);
    return await result;
  },
  async getFTLEditSettings() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/getFTLEditSettings`)
      .then((res) => res.data);
    return result;
  },
  async getCustomer(customerID){
    let result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/getCustomer?customerID=${customerID}`)
    .then(res=>res.data);
    return result;
  },
  async updateCustomer(params){
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/updateCustomer`, params)
      .then((res) => res.data);
    return result;
  },
  async GetAllFeaturesByUser() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/features/GetAllFeaturesByUser`
        , {
          params: {
            //ldi5 applicationid is 9 in feature management.
            applicationid: 9
          },
        })
      .then((res) => res.data);
    return result;
  },
  async getDormantCustomers(data) {
    let result = await axiosConfig
    .get(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/getdormantcustomers/",data);
    return result;
  },
  async getCustomersTracking(data) {
    let result = await axiosConfig
    .get(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/getdormantcustomerstracking/",data);
    return result;
  },
  async updatedormantcustomerinactive(data) {
    let result = await axiosConfig
    .post(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/updatedormantcustomerinactive/",data);
    return result;
  },
  async getActivePortals(data) {
    let result = await axiosConfig
    .get(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/getactiveportals/",data);
    return result;
  },
  async unassignagent(data) {
    let result = await axiosConfig
    .post(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/unassignagent/",data);
    return result;
  },
  async assignagent(data) {
    let result = await axiosConfig
    .post(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/assignagent/",data);
    return result;
  },
  async getdormantcustomerdashBoard(data) {
    let result = await axiosConfig
    .get(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/getdormantcustomerdashBoard/",data);
    return result;
  },

  async getallcustomercontacts(data) {
    let result = await axiosConfig
      .get(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/getallcustomercontacts/" + data);
    return result;
  },
  async getdormantcustomernotes(data) {
    let result = await axiosConfig
    .get(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/getdormantcustomernotes/"+ data);
    return result;
  },
  async insertdormantcustomernotes(data) {
    let result = await axiosConfig
      .post(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/insertdormantcustomernotes/", data);
    return result;
  },
  async getdormantcustomerreport(customerID,datatype,returntype) 
  {
    let result = await axiosConfig
    .get(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/getdormantcustomerreport?customerID="+ customerID + "&reportReturnType=" + datatype + "&reportReturnDataType=" + returntype);
    return result;
  },
  async  SendDormantCustomerReport(data) {
    let result = await axiosConfig
    .post(process.env.VUE_APP_API_WEB_URL + "/dormantcustomers/senddormantcustomerreport/",data);
    return result;
  },
  async getAccountManagers(portalID){
    let result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/getAccountManagers?portalID=${portalID}`)
    .then(res=>res.data);
    return result;
  },
  async getCustomerBillingInstructions(customerID){
    const result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/${customerID}/customerBillingInstructions`);
    return result.data
  },
  async getCustomerLTLSettings(customerID){
    const result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/${customerID}/customerLTLSettings`);
    return result.data
  },
  async getCustomerOverview(customerID) {
    let result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/${customerID}/overview`)
      .then(res => res.data);
    return result;
  },
  async downloadExcel(customerId, days) {
    const result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/${customerId}/overview/downloadTotalInvoices?days=${days}`, {
      responseType: 'blob'
    })
    return result.data;
  },
  async getAllCustomerPortalContacts(customerID){
    const result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/customerContacts/${customerID}`);
    return result.data
  },
  async getCustomerProfileNotes(customerID){
    const result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/${customerID}/customerNotes`);
    return result.data
  },
  async getCustomerPerformance(customerID){
    const result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/${customerID}/performance`);
    return result.data
  },
  async getParentAccountDetails(filter,pageNumber,pageSize){
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/customerAccounts`
        , {
            Filter:filter,
            PageNumber: pageNumber,
            PageSize: pageSize
          
        })
      .then((res) => res.data);
    return result;
  },
  async getNAICSDetails() {
    let result = await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/NAICSData`)
      .then((res) => res.data);
    return result;
  },
  async getGeneralTab(customerID){
    let result = await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/${customerID}/generalInfo`)
    .then(res=>res.data);
    return result;
  },
  async getCustomerDocuments(params){
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/Customer/customerProfile/documents`, params);
    return result.data
  },

  async searchInvoices(searchInvoicesRequest){
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/invoices`, searchInvoicesRequest);
    return result;
  },
  async searchCarriers(searchCarriersRequest){
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/carriers`, searchCarriersRequest);
    return result;
  },
  async getLoadFilters(portalID = null){
    let loadFilterRequest = {};
    if (Array.isArray(portalID) && portalID.length > 0) {
      loadFilterRequest.PortalIDList = portalID.map(value => ({
        TextField: value,
        MatchMode: 'exact'
      }));
    }

    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/loadFilters`,loadFilterRequest);
    return result;
  },

  async searchLoads(params) {
    const event = new CustomEvent('searchMyLoads');
    var result = [];
    await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/myLoads`, {
        OriginalEvent: {
          Page: params.PageNumber,
          Rows: params.PageSize,
        },
        Page: params.PageNumber,
        Rows: params.PageSize,
        SortOrder:
          params.SortOrder == null ? 1 : params.SortOrder,
        SortField: params.OrderByColumn,
        Filters: params.filters,
        MultiSortMeta: {},
        ProxyPortals: params.filters.PortalID?.map(item => item.Value),
        SearchLoadsRequest: true,
      })

      .then((res) => {
        if (res.data && Array.isArray(res.data.DataHeader)) {
          res.data.DataHeader = res.data.DataHeader.filter((i)=>{
            if (i.FieldName!='Origin'
            && i.FieldName!='Destination'
            && i.FieldName!='LoadID'){
            return i}
          })
        }
        result = res.data;
        if (!params?.dontDispatchGetMyLoadsEvent) {
          window.dispatchEvent(event);
        }
      });
    return result;
  },

  async getMyLoadsFilters(portalID) {
    let loadFilterRequest = {};
    loadFilterRequest.PortalIDList = portalID
    
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/myLoadsFilters`,loadFilterRequest);
    return result;
  },
  
  async getCarrierFilters(){
    const carrierFilterRequest = {};
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/carrierFilters`,carrierFilterRequest);
    return result;
  },

  async searchAccounts(searchAccountsRequest){
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/protectedAccounts`, searchAccountsRequest);
    return result;
  },
  
  async searchCustomers(searchCustomerRequest){
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/customers`, searchCustomerRequest);
    return result;
  },
  async getCustomerFilters(portalID){

    const customerFilterRequest = {};
    customerFilterRequest.PortalId = [{
      TextField: portalID,
      MatchMode: 'exact'
    }];
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/customerFilters`,customerFilterRequest);
    return result;
  },
  async searchEmployees(searchEmployeeRequest){
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/employees`, searchEmployeeRequest);
    return result;
  },

  async getEmployeeFilters() {
    let employeeFilterRequest = {};
    const result = await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/SearchScreen/employeeFilters`, employeeFilterRequest);
    return result;
  },
  async saveIMLoadBookEdit(loadDetailRequest) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/saveLoadBookEdit`,
        loadDetailRequest
      ).then(res => res.data)
    return result;
  },
  async getEditIMLoadDetails(loadID) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getCurrentLoad`, {
        LoadID: loadID
      })
      .then(res => res.data);
    return result;
  },
  async getDrayTypes(IDs = []) {
    return await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getDrayTypes`, {
        params: IDs,
        paramsSerializer: IDSerializer
      });
  },
  async getIEDs(IDs = []) {
    return await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getIEDs`, {
        params: IDs,
        paramsSerializer: IDSerializer
      });
  },
  async getInBonds(IDs = []) {
    return await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getInBonds`, {
        params: IDs,
        paramsSerializer: IDSerializer
      });
  },
  async getMoveTypes(IDs = []) {
    return await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getMoveTypes`, {
        params: IDs,
        paramsSerializer: IDSerializer
      });
  },
  async getRailPlans(IDs = []) {
    return await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getRailPlans`, {
        params: IDs,
        paramsSerializer: IDSerializer
      });
  },
  async getRailRamps(IDs = []) {
    return await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getRailRamps`, {
        params: IDs,
        paramsSerializer: IDSerializer
      });
  },
  async getEquipmentDescriptions(IDs = []) {
    return await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/getEquipmentDescriptions`, {
        params: IDs,
        paramsSerializer: IDSerializer
      });
  },
  async generateRailD2DDispatch(request) {
    return await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/generateD2DRailDispatch`,
        request
      );
  },
  async generateOriginDrayDispatch(request) {
    return await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/generateOriginDrayDispatch`,
        request
      );
  },
  async generateDestDrayPreNote(request) {
    return await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/generateDestDrayPreNote`,
        request
      );
  },
  async generateInterDrayPreNote(request) {
    return await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/generateInterDrayPreNote`,
        request
      );
  },  
  async generateRailManifestBill(request) {
    return await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/generateRailManifestBill`,
        request
      );
  },
  async queueEDI404(request) {
    return await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/queueEDI404`,
        request
      );
  },
  async getEDI404(loadID){
    return (await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/EDI404/${loadID}`)).data.Data;
  },
  async getDoorToDoorCarriers() {
    return (await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/DoorToDoorCarriers`)).data.Data;
  },
  async getRampToRampCarriers() {
    return (await axiosConfig
      .get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/RampToRampCarriers`)).data.Data;
  },

  async createCheckCall(request) {
    return await axiosConfig.post(`${process.env.VUE_APP_API_WEB_URL}/trackloads/checkCall`,
      request
    );
  },
  async getCheckCalls(loadID) {
    return (await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/trackloads/checkCall/${loadID}`)).data.Data;
  },

  async getPortalAIAgents(portalID) {
    return (await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/AIAgent/getPortalAIAgents/${portalID}`)).data.Data;
  },

  async getLoadAIAgent(loadID) {
    return (await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/AIAgent/getLoadAIAgent/${loadID}`)).data.Data;
  },

  async advanceLoadDates(request) {
    let result = await axiosConfig
      .post(`${process.env.VUE_APP_API_WEB_URL}/TrackLoads/advanceLoadDates`, request)
      .then((res) => res.data);
    return result;
  },
  async getCustomsBrokerByCustomer(CustomerID) {
    return (await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/CustomsBrokerByCustomer/${CustomerID}`)).data.Data;
  },
  async saveCustomsBroker(brokerData) {
    const result = await axiosConfig.post(
      `${process.env.VUE_APP_API_WEB_URL}/Intermodal/customsBroker`,
      brokerData
    ).then((res) => res.data);
    return this.processServiceResponse(result, { showSuccess: true, successDetail: 'Customs Broker created successfully.' });
  },
    async getIntermodalLoadEvents(loadID) {
    return (await axiosConfig.get(`${process.env.VUE_APP_API_WEB_URL}/Intermodal/LoadEvent/${loadID}`)).data.Data;
  },
};

export { dataProvider };
